import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useState } from 'react'
import { useHall, useRemoveHallMeter, useSaveHallMeter } from '@/api/hooks'
import type { Hall, HallMeter } from '@/api/types'
import { ErrorBanner } from '@/components/common'

/**
 * The electricity meters on a space.
 *
 * <p>A hall is rarely one meter. There is the hall itself, usually a kitchen
 * behind it, sometimes the lift and the pumps on a third - each its own board
 * with its own dial, read separately and often geared differently. The space
 * carried a single number, so a venue with three had all three readings
 * recorded under whatever somebody typed that day.</p>
 *
 * <p>A meter withdrawn here stops being offered but is not deleted: every
 * reading ever taken on it points at it, and a board taken out of service does
 * not unmake the units that were billed through it.</p>
 */
export function MeterEditor({ space }: { space: Hall }) {
  // Read the space back rather than trusting the object the dialog opened with:
  // adding a meter changes it, and a stale copy shows the list as it was.
  const { data: live } = useHall(space.id)
  const save = useSaveHallMeter()
  const remove = useRemoveHallMeter()

  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [meterNumber, setMeterNumber] = useState('')
  const [multiplyingFactor, setMultiplyingFactor] = useState('1')
  const [billable, setBillable] = useState(true)

  const meters = (live ?? space).meters ?? []
  const valid = Boolean(name.trim()) && Number(multiplyingFactor || 0) > 0

  const clear = () => {
    setOpen(false)
    setEditingId(null)
    setName('')
    setMeterNumber('')
    setMultiplyingFactor('1')
    setBillable(true)
  }

  const edit = (meter: HallMeter) => {
    setEditingId(meter.id)
    setName(meter.name)
    setMeterNumber(meter.meterNumber ?? '')
    setMultiplyingFactor(String(meter.multiplyingFactor ?? 1))
    setBillable(meter.billable)
    setOpen(true)
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Electricity meters</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {!open ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Add a meter
          </Button>
        ) : null}
      </Stack>

      <ErrorBanner error={save.error ?? remove.error} />

      {meters.length === 0 && !open ? (
        <Typography variant="body2" color="text.secondary">
          No meters yet. Until one is added here, a reading falls back to this hall's legacy single meter.
        </Typography>
      ) : null}

      <Stack spacing={0.5} sx={{ mb: open ? 2 : 0 }}>
        {meters.map((meter) => (
          <Stack key={meter.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 120 }}>
              {meter.name}
            </Typography>
            {meter.meterNumber ? (
              <Typography variant="caption" color="text.secondary">
                {meter.meterNumber}
              </Typography>
            ) : null}
            {/* A geared meter read as though it were direct under-bills every
                event on it, so the factor is on the face of the list. */}
            {Number(meter.multiplyingFactor) !== 1 ? (
              <Chip size="small" variant="outlined" label={`×${meter.multiplyingFactor}`} />
            ) : null}
            {!meter.billable ? (
              <Chip size="small" variant="outlined" label="Not billed" />
            ) : null}
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Correct this meter">
              <IconButton size="small" onClick={() => edit(meter)} aria-label={`Edit ${meter.name}`}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Take it out of service; its readings stay on the record">
              <IconButton
                size="small"
                color="error"
                aria-label={`Withdraw ${meter.name}`}
                onClick={() => remove.mutate({ hallId: space.id, meterId: meter.id })}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      {open ? (
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="What it is called"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Hall"
              helperText="Hall, Kitchen, Lift and pumps"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Meter number"
              value={meterNumber}
              onChange={(event) => setMeterNumber(event.target.value)}
              helperText="What the board says on it"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Multiplier"
              value={multiplyingFactor}
              onChange={(event) => setMultiplyingFactor(event.target.value)}
              helperText="1 unless CT metered"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={billable}
                  onChange={(event) => setBillable(event.target.checked)}
                />
              }
              label="Billed"
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={clear}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!valid || save.isPending}
                onClick={() =>
                  save.mutate(
                    {
                      hallId: space.id,
                      meterId: editingId ?? undefined,
                      body: {
                        name: name.trim(),
                        meterNumber: meterNumber.trim() || undefined,
                        multiplyingFactor: Number(multiplyingFactor || 1),
                        billable,
                        sortOrder: meters.length,
                      },
                    },
                    { onSuccess: clear },
                  )
                }
              >
                {editingId ? 'Save' : 'Add it'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      ) : null}
    </Box>
  )
}
